import React, { useState } from "react";
import { Link } from "react-router-dom";
import {
  GroupPermissionsFragment,
  permissionGroups,
} from "../../app/permissions-fragment";

export const DepositOverview = ({
  deposit,
  loading,
  edit,
  fetchDeposit,
  comment,
  commentLoading,
  commentMessage,
  saveComment
}) => {
  const [localComment, setLocalComment] = useState(comment);

  const paymentAccountsRequested =
    deposit.kyc_verification_statuses?.payment_accounts || [];

  const totalSentToReal = (deposit.bonus_into_real_transactions || []).reduce(
    (acc, f) => acc + +f.amount,
    0
  );

  React.useEffect(() => {
    setLocalComment(comment);
  }, [comment]);

  const handleSaveComment = () => {
    saveComment(localComment);
  };

  const handleCommentChange = (e) => {
    setLocalComment(e.target.value);
  };

  return (
    <section className="overview">
      <div className="row">
        {/* Information Column */}
        <div className="col-md-4">
          <h2>Information</h2>

          <div className="line-item">
            <div className="label">NAME</div>
            <div className="value">
              <Link target={"_blank"} to={`/users/${deposit.user?.id}`}>
                {deposit.user?.full_name}
              </Link>
            </div>
          </div>

          <div className="line-item">
            <div className="label">AGE</div>
            <div className="value">{deposit.user?.age}</div>
          </div>

          <div className="line-item">
            <div className="label">TAX ID</div>
            <div className="value">{deposit.tax_id}</div>
          </div>

          <div className="line-item">
            <div className="label">DEPOSIT STATUS</div>
            <div className="value">{deposit.status}</div>
          </div>

          <div className="line-item">
            <div className="label">DEPOSIT AMOUNT</div>
            <div className="value">
              {deposit.type} - {deposit.amount} {deposit.currency_type}
            </div>
          </div>

          <div className="line-item">
            <div className="label">LAST DEPOSIT</div>
            <div className="value">
              {deposit.last_deposit_method} - {deposit.last_deposit_amount}{" "}
              {deposit.currency_type}
            </div>
          </div>

          <div className="line-item">
            <div className="label">TOTAL COMPLETED DEPOSITS</div>
            <div className="value">
              {deposit.total_completed_deposits || deposit.total_deposited}{" "}
              {deposit.currency_type}
            </div>
          </div>

          <div className="line-item">
            <div className="label">TOTAL COMPLETED WITHDRAWALS</div>
            <div className="value">
              {deposit.total_completed_withdrawals || deposit.total_withdrawn}{" "}
              {deposit.currency_type}
            </div>
          </div>

          <div className="line-item">
            <div className="label">REAL WAGERED</div>
            <div className="value">
              {deposit.real_wagered} {deposit.currency_type}
            </div>
          </div>

          <div className="line-item">
            <div className="label">REAL WAGERED SINCE LAST DEPOSIT</div>
            <div className="value">
              {deposit.real_wagered_since_last_deposit} {deposit.currency_type}
            </div>
          </div>

          <div
            className={`line-item ${Math.abs(deposit.real_net_profit) >=
              +deposit.total_deposited * 10 && "red"}`}
          >
            <div className="label">REAL NET PROFIT (NGR)</div>
            <div className="value">
              {deposit.real_net_profit} {deposit.currency_type}
            </div>
          </div>

          <div className="line-item comment-section">
            <div className="label">COMMENT</div>
            <div className="value">
              <textarea
                className="comment-textarea"
                value={localComment}
                onChange={handleCommentChange}
                placeholder="Add your observations or notes about this deposit..."
                rows={3}
                disabled={commentLoading}
              />
              <div className="comment-actions">
                <button
                  className="save-comment-btn"
                  onClick={handleSaveComment}
                  disabled={commentLoading || !localComment?.trim()}
                >
                  {commentLoading ? 'Saving...' : 'Save'}
                </button>
              </div>
              {commentMessage && (
                <div className={`comment-message ${commentMessage.type}`}>
                  {commentMessage.text}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Fraud Checks Column */}
        <GroupPermissionsFragment
          group={permissionGroups.PAYMENTS.DEPOSITS}
          fallbackComponent={<div className="col-md-4"></div>}
        >
          <div className="col-md-4">
            <h2>Fraud Checks</h2>

            <div className={`line-item ${+deposit.last_deposit_vs_wagered <= 3 && "red"}`}>
              <div className="label">LAST DEPOSIT VS WAGERED SINCE LAST DEPOSIT</div>
              <div className="value">{deposit.last_deposit_vs_wagered}x</div>
            </div>

            <div className={`line-item ${+deposit.total_deposit_vs_wagered <= 3 && "red"}`}>
              <div className="label">TOTAL DEPOSIT VS TOTAL WAGERED</div>
              <div className="value">{deposit.total_deposit_vs_wagered}x</div>
            </div>

            <div className="line-item">
              <div className="label">AMOUNT DEPOSITED WITH CARD</div>
              <div className="value">
                {deposit.amount_deposited_with_card} {deposit.currency_type}
              </div>
            </div>

            <div className="line-item">
              <div className="label">PERCENT CARD DEPOSITS</div>
              <div className="value">{deposit.percent_card_deposits}%</div>
            </div>

            <div className={`line-item ${totalSentToReal > 0 && "red"}`}>
              <div className="label">TOTAL FLAGGED BONUS MONEY PAID TO REAL</div>
              <div className="value">{totalSentToReal.toFixed(4)}</div>
            </div>

            <div className={`line-item ${deposit.has_transferred_bonus && "red"}`}>
              <div className="label">BONUS TRANSFERRED</div>
              <div className="value">
                {typeof deposit.has_transferred_bonus === "boolean"
                  ? deposit.has_transferred_bonus
                    ? "true"
                    : "false"
                  : deposit.has_transferred_bonus}
              </div>
            </div>

            <div className="line-item">
              <div className="label">TOTAL BONUS TRANSFERRED</div>
              <div className="value">{deposit.total_transferred_bonus}</div>
            </div>

            <div className="line-item">
              <div className="label">CURRENT BALANCE</div>
              <div className="value">
                {deposit.current_balance} {deposit.currency_type}
              </div>
            </div>

            <div className="line-item">
              <div className="label">EXPECTED BALANCE</div>
              <div className="value">
                {deposit.expected_balance} {deposit.currency_type}
              </div>
            </div>

            <div className={`line-item ${deposit.has_pending_deposits && "red"}`}>
              <div className="label">ADDITIONAL PENDING DEPOSITS</div>
              <div className="value">
                {deposit.has_pending_deposits ? "true" : "false"}
              </div>
            </div>

            <div className={`line-item ${deposit.bets_refunded_to_placed_ratio > 0.2 && "red"}`}>
              <div className="label">BETS REFUNDED TO PLACED RATIO</div>
              <div className="value">
                {deposit.bets_refunded_to_placed_ratio}
              </div>
            </div>

            <div className={`line-item ${deposit.email_changed_last_30_days && "red"}`}>
              <div className="label">EMAIL CHANGED LAST 30 DAYS</div>
              <div className="value">
                {deposit.email_changed_last_30_days ? "true" : "false"}
              </div>
            </div>

            <div className={`line-item ${deposit.tax_id_changed_last_30_days && "red"}`}>
              <div className="label">TAX ID CHANGED LAST 30 DAYS</div>
              <div className="value">
                {deposit.tax_id_changed_last_30_days ? "true" : "false"}
              </div>
            </div>

            <div className={`line-item ${deposit.has_zero_amount_bet && "red"}`}>
              <div className="label">HAS ZERO AMOUNT BETS</div>
              <div className="value">
                {deposit.has_zero_amount_bet ? "true" : "false"}
              </div>
            </div>

            <div className={`line-item ${deposit.has_long_running_bets && "red"}`}>
              <div className="label">HAS LONG RUNNING BETS</div>
              <div className="value">
                {deposit.has_long_running_bets ? "true" : "false"}
              </div>
            </div>

            {deposit.eur_weekly_cashback_received !== undefined && (
              <div className="line-item">
                <div className="label">EUR WEEKLY CASHBACK RECEIVED</div>
                <div className="value">
                  {deposit.eur_weekly_cashback_received}
                </div>
              </div>
            )}

            {deposit.real_wagered_in_last_weekly_cashback_period !== undefined && (
              <div className="line-item">
                <div className="label">
                  REAL EUR WAGERED IN LAST WEEKLY CASHBACK PERIOD
                </div>
                <div className="value">
                  {deposit.real_wagered_in_last_weekly_cashback_period}
                </div>
              </div>
            )}
          </div>
        </GroupPermissionsFragment>

        {/* Compliance Column */}
        <GroupPermissionsFragment
          group={permissionGroups.PAYMENTS.DEPOSITS}
          fallbackComponent={<div className="col-md-4"></div>}
        >
          <div className="col-md-4">
            <h2>Compliance</h2>

            <div className="line-item">
              <div className="label">AMOUNT WITHDRAWN</div>
              <div className="value">
                {deposit.total_withdrawn} {deposit.currency_type}
              </div>
            </div>

            <div className="line-item">
              <div className="label">AMOUNT DEPOSITED</div>
              <div className="value">
                {deposit.total_deposited} {deposit.currency_type}
              </div>
            </div>

            <div className="line-item">
              <div className="label">ID</div>
              <div className="value">
                {deposit.kyc_verification_statuses?.id?.requested
                  ? deposit.kyc_verification_statuses.id.status
                  : "Not requested"}
              </div>
            </div>

            <div className="line-item">
              <div className="label">ADDRESS</div>
              <div className="value">
                {deposit.kyc_verification_statuses?.address?.requested
                  ? deposit.kyc_verification_statuses.address.status
                  : "Not requested"}
              </div>
            </div>

            <div className="line-item">
              <div className="label">PAYMENT METHODS</div>
              <div className="value">
                {paymentAccountsRequested.length > 0 ? (
                  <ul>
                    {paymentAccountsRequested.map(
                      ({ type, masked_account, status }, idx) => (
                        <li key={idx}>
                          • {type} - {masked_account} - {status}
                        </li>
                      )
                    )}
                  </ul>
                ) : (
                  "None requested"
                )}
              </div>
            </div>

            <div className="line-item">
              <div className="label">TAX IDS</div>
              <div className="value">
                {deposit.tax_ids && deposit.tax_ids.length > 0 ? (
                  <ul>
                    {deposit.tax_ids.map((result, idx) => (
                      <li key={idx}>{result}</li>
                    ))}
                  </ul>
                ) : (
                  ""
                )}
              </div>
            </div>

            <div className="line-item">
              <div className="label">PEP MATCHES</div>
              <div className="value">
                {deposit.pep_sip_check?.number_of_pep_matches}
              </div>
            </div>

            <div className="line-item">
              <div className="label">SIP MATCHES</div>
              <div className="value">
                {deposit.pep_sip_check?.number_of_sip_matches}
              </div>
            </div>

          </div>
        </GroupPermissionsFragment>
      </div>
    </section>
  );
};